import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Grid, Modal, Skeleton } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LoadingButton } from '@mui/lab';
import { FileDownloadOutlined } from '@mui/icons-material';
import SucessModal from 'src/components/sucess-modal';
import { getCensusBatchRetrieve } from 'src/services/apiServices/censusReportService/getCensusBatchRetrieve';
import { getPullCensusReport } from 'src/services/apiServices/censusReportService/getPullCensusReport';
import { ICensusBatchRetrieveResponse } from 'src/services/apiServices/censusReportService/types';
import moment from 'moment';
import 'moment-timezone';

const DailyCensusDownload = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [lastRefreshed, setLastRefreshed] = useState<string>('');
  const [retrieveLoading, setRetrieveLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [facilityName, setFacilityName] = useState<string>('');
  const [snackbarSuccess, setSnackbarSuccess] = useState<boolean>(false);
  const [snackbarError, setSnackbarError] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    title: '',
  });

  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  const getFacilityName = useCallback(() => {
    const getFacilityUuid = localStorage.getItem('facilityId');
    const facility = getAssignedFacilities.response?.data?.data?.find(
      (fac: any) => fac.uuid === getFacilityUuid
    );

    return facility?.name ?? '';
  }, [getAssignedFacilities]);

  useEffect(() => {
    setFacilityName(getFacilityName());
  }, [getFacilityName]);

  // Fetch availability metadata on mount, on date change, and on facility change.
  // `getAssignedFacilities` is included in deps so a facility switch (which updates
  // the redux selection) triggers a re-fetch; the selected date is intentionally
  // preserved.
  useEffect(() => {
    let isCancelled = false;

    if (!selectedDate) return;

    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

    setRetrieveLoading(true);

    getCensusBatchRetrieve({ date: formattedDate })
      .then((response) => {
        if (isCancelled) return;

        const data = response?.data as ICensusBatchRetrieveResponse;
        const modified = data?.batch_information?.modified;
        const hasFiles = Array.isArray(data?.census_files) && data.census_files.length > 0;

        if (hasFiles && modified) {
          setIsAvailable(true);
          const facilityTimeZone = localStorage.getItem('facility_timezone') || '';
          const refreshed = facilityTimeZone
            ? moment.utc(modified).tz(facilityTimeZone)
            : moment.utc(modified).local();
          setLastRefreshed(refreshed.format('MM/DD/YYYY, h:mm A'));
        } else {
          setIsAvailable(false);
          setLastRefreshed('');
        }
      })
      .catch((error) => {
        if (isCancelled) return;

        // Treat 404 as "not available" rather than surfacing an error toast,
        // mirroring the SLM card behavior.
        if (error?.response?.status !== 404) {
          setSnackbarError(true);
          setSnackbarSuccess(false);
          setSnackbar((prev) => ({
            ...prev,
            open: true,
            title: '',
            message:
              'Something went wrong with retrieving the census report. Please refresh or let our support team know if the issue persists.',
          }));
        }
        setIsAvailable(false);
        setLastRefreshed('');
      })
      .finally(() => {
        if (!isCancelled) {
          setRetrieveLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, getAssignedFacilities]);

  const handleChangeDate = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleDownload = () => {
    setDownloadLoading(true);

    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

    getPullCensusReport({ date: formattedDate })
      .then((res) => {
        const trimmedFacilityName = facilityName.trim();
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${trimmedFacilityName}_Daily_Census_${moment(selectedDate).format('MM-DD-YYYY')}.zip`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSnackbarSuccess(true);
        setSnackbarError(false);
        setSnackbar({
          open: true,
          title: 'Census Report Downloaded!',
          message: `${facilityName} Daily Census Report for ${moment(selectedDate).format(
            'MM/DD/YYYY'
          )} successfully downloaded!`,
        });
      })
      .catch(() => {
        setSnackbarSuccess(false);
        setSnackbarError(true);
        setSnackbar({
          open: true,
          title: 'Error Downloading Census Report',
          message:
            'Something went wrong with downloading the census report. Please refresh or let us know via Intercom if the issue persists.',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const getStatusMessage = (): string => {
    if (!isAvailable || !lastRefreshed) {
      return 'No census report is available for the selected date.';
    }

    const selected = moment(selectedDate).startOf('month');
    const monthsDiff = moment().startOf('month').diff(selected, 'months');

    if (monthsDiff <= 1) {
      return `Report last refreshed on: ${lastRefreshed}. Scheduled to refresh again tonight.`;
    }

    return `Report last refreshed on: ${lastRefreshed}. Submit a ticket to refresh this report.`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100px',
      }}
    >
      <Box sx={{ flex: 1 }} />

      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Grid
          container
          spacing={2}
          alignItems="flex-end"
          direction={{ xs: 'column', md: 'row' }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={selectedDate}
                onChange={handleChangeDate}
                maxDate={new Date()}
                format="MM/dd/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small',
                    sx: {
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        minHeight: '40px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              alignItems: 'flex-end',
            }}
          >
            <LoadingButton
              disabled={downloadLoading || retrieveLoading || !isAvailable}
              startIcon={<FileDownloadOutlined />}
              variant="contained"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                height: 35,
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 120 },
                ':hover': {
                  background: '#6366F1',
                },
                '.MuiLoadingButton-loadingIndicator': {
                  left: 15,
                },
              }}
              loadingPosition="start"
              onClick={handleDownload}
              loading={downloadLoading}
            >
              Download
            </LoadingButton>
          </Grid>
        </Grid>

        {/* Data refresh status text below the field row */}
        <Box sx={{ mt: 2 }}>
          {retrieveLoading ? (
            <Box sx={{ marginTop: 2 }}>
              <Skeleton
                animation="wave"
                width={'20%'}
              />
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ marginTop: 2 }}
            >
              {getStatusMessage()}
            </Typography>
          )}
        </Box>
      </Box>

      <Modal open={snackbar.open}>
        <SucessModal
          close={() => setSnackbar({ ...snackbar, open: false })}
          title={snackbar.title}
          message={snackbar.message}
          isSucess={snackbarSuccess && !snackbarError}
        />
      </Modal>
    </Box>
  );
};

export default DailyCensusDownload;
